import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import { formatWon } from "Components/Sales/salesFormat";
import unwrapResponse from "utils/unwrapResponse";
import { catchError } from "JS/common";
import { getSalesCustomerPayments } from "JS/API";

const PAYMENT_TYPE_LABEL = { order: "등록대행", added: "추가결제", review: "검토요청", manual: "수동" };
const STATUS_LABEL = { paid: "결제완료", partial_refund: "부분 환불", refund: "환불완료" };
const SERVICE_LABEL = { trademark: "상표", patent: "특허", design: "디자인", other: "기타" };
const LIMIT = 20;

const styles = {
  overlay: {
    position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
  },
  modal: {
    backgroundColor: "#ffffff", borderRadius: "16px", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    width: "100%", maxWidth: "860px", maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px",
    borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc",
  },
  title: { fontSize: "16px", fontWeight: "600", color: "#1e293b" },
  sub: { fontSize: "12px", color: "#64748b", marginTop: "2px" },
  closeBtn: { border: "none", background: "transparent", cursor: "pointer", color: "#64748b", padding: "6px" },
  content: { flex: 1, overflow: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", fontVariantNumeric: "tabular-nums" },
  th: { textAlign: "left", padding: "10px 14px", fontSize: "12px", color: "#6b7280", fontWeight: "600", backgroundColor: "#f8fafc", borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap" },
  td: { padding: "10px 14px", borderBottom: "1px solid #f1f5f9", color: "#1f2937" },
  right: { textAlign: "right" },
  row: { cursor: "pointer" },
  tag: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: "600" },
  dot: { width: "8px", height: "8px", borderRadius: "2px", display: "inline-block" },
  muted: { color: "#9ca3af" },
  green: { color: "#059669" },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", padding: "12px", borderTop: "1px solid #e2e8f0", fontSize: "13px", color: "#64748b" },
  pageBtn: { padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" },
  loading: { padding: "60px", textAlign: "center", color: "#64748b" },
};

const SalesCustomerPaymentsModal = ({ customerKey, customerName, filter, service, onClose }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getSalesCustomerPayments(customerKey, filter, { service, page, limit: LIMIT });
      if (typeof result !== "object" || !result?.data) {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      setData(unwrapResponse(result));
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [customerKey, filter, service, page, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRowClick = (item) => {
    if ((item.payment_type === "order" || item.payment_type === "added") && item.apply_agent_pk) {
      navigate(`/agencies/${item.apply_agent_pk}`);
    } else if (item.payment_type === "review" && item.review_request_id) {
      navigate(`/review-self/${item.review_request_id}`);
    }
  };

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  const customer = data?.customer;
  const totalPages = data?.pagination?.total_pages || 1;

  return (
    <>
      <div style={styles.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={styles.modal} role="dialog" aria-label="고객 결제 이력">
          <div style={styles.header}>
            <div>
              <div style={styles.title}>{customer?.user_name || customerName} 결제 이력</div>
              <div style={styles.sub}>
                {customer?.is_manual ? `수동 결제 · ${customer?.contact || "연락처 없음"}` : customer?.user_id}
                {data?.pagination ? ` · 총 ${data.pagination.total}건` : ""}
              </div>
            </div>
            <button type="button" aria-label="닫기" style={styles.closeBtn} onClick={onClose}><FaTimes size={16} /></button>
          </div>
          <div style={styles.content}>
            {isLoading || !data ? (
              <div style={styles.loading}>로딩 중...</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>결제일</th>
                    <th style={styles.th}>유형</th>
                    <th style={{ ...styles.th, ...styles.right }}>매출</th>
                    <th style={{ ...styles.th, ...styles.right }}>순매출</th>
                    <th style={styles.th}>상태</th>
                    <th style={styles.th}>서비스</th>
                    <th style={styles.th}>태그</th>
                    <th style={styles.th}>메모</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr
                      key={`${item.payment_type}-${item.payment_pk}`}
                      style={item.payment_type === "manual" ? undefined : styles.row}
                      onClick={() => handleRowClick(item)}
                    >
                      <td style={styles.td}>{item.paid_at}</td>
                      <td style={styles.td}>{PAYMENT_TYPE_LABEL[item.payment_type] || item.payment_type}</td>
                      <td style={{ ...styles.td, ...styles.right }}>{formatWon(item.gross)}</td>
                      <td style={{ ...styles.td, ...styles.right, ...styles.green }}>{formatWon(item.net)}</td>
                      <td style={styles.td}>{STATUS_LABEL[item.status] || item.status}</td>
                      <td style={styles.td}>{SERVICE_LABEL[item.service_type] || item.service_type}</td>
                      <td style={styles.td}>
                        {item.tag ? (
                          <span style={styles.tag}><span style={{ ...styles.dot, backgroundColor: item.tag.color }} />{item.tag.name}</span>
                        ) : <span style={styles.muted}>-</span>}
                      </td>
                      <td style={{ ...styles.td, ...styles.muted }}>{item.description || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {data && totalPages > 1 && (
            <div style={styles.pagination}>
              <button type="button" style={styles.pageBtn} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                <FaChevronLeft size={10} /> 이전
              </button>
              <span>{page} / {totalPages} 페이지</span>
              <button type="button" style={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                다음 <FaChevronRight size={10} />
              </button>
            </div>
          )}
        </div>
      </div>
      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={handleAlertOk} />
      )}
    </>
  );
};

export default SalesCustomerPaymentsModal;
